<template>
  <div class="login-screen onboarding-screen">
    <a href="#onboarding-main-content" class="cg-skip-link">Перейти к содержимому</a>
    <header class="login-header">
      <div class="content-read-max login-header__inner onboarding-header__inner">
        <img :src="brandLogoLight" alt="CardGenius" class="login-header__brand dark:hidden" />
        <img :src="brandLogoDark" alt="CardGenius" class="login-header__brand hidden dark:block" />
      </div>
    </header>

    <main id="onboarding-main-content" class="login-main" tabindex="-1">
      <div class="login-content content-read-max">
        <section class="login-card onboarding-card" aria-label="Выбор площадки для контента">
          <div class="login-card__header onboarding-card__header">
            <h1 class="login-card__title">Добро пожаловать в CardGenius</h1>
            <p class="login-card__subtitle onboarding-card__subtitle">Куда создаем контент?</p>
            <p class="onboarding-card__hint">Выберите площадку — подстроим требования. Или создайте универсальный контент.</p>
          </div>

          <div class="onboarding-marketplace" role="group" aria-label="Площадки для создания контента">
            <button
              v-for="option in marketplaceOptions"
              :key="option.id"
              type="button"
              class="onboarding-marketplace__option"
              :disabled="selecting !== null"
              @click="handleSelect(option.id)"
              @pointerenter="marketplaceModalOptionHovered(option.id)"
            >
              <span class="onboarding-marketplace__icon">
                <img
                  v-if="option.logo"
                  :src="option.logo"
                  :alt="option.label"
                  class="onboarding-marketplace__logo"
                  aria-hidden="true"
                />
                <span
                  v-else-if="option.icon"
                  class="onboarding-marketplace__icon-fallback"
                  aria-hidden="true"
                >
                  <Icon :name="option.icon" :size="22" weight="regular" />
                </span>
                <span v-else class="onboarding-marketplace__icon-fallback onboarding-marketplace__icon-fallback--text">
                  {{ option.shortLabel }}
                </span>
              </span>
              <span class="onboarding-marketplace__copy">
                <span class="onboarding-marketplace__label">{{ option.label }}</span>
                <span v-if="option.hint" class="onboarding-marketplace__description">{{ option.hint }}</span>
              </span>
            </button>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import brandLogoDark from "@/assets/branding/Full/Dark.svg";
import brandLogoLight from "@/assets/branding/Full/light.svg";
import kaspiLogo from "@/assets/marketplaces/kaspi.svg";
import flipLogo from "@/assets/marketplaces/flip.svg";
import olxLogo from "@/assets/marketplaces/olx.svg";
import ozonLogo from "@/assets/marketplaces/ozon.svg";
import wbLogo from "@/assets/marketplaces/wb.svg";
import Icon from "@/components/ui/Icon.vue";
import { onboardingCompleted, onboardingScreenHidden, onboardingScreenViewed } from "@/lib/onboardingAnalytics";
import { marketplaceModalOptionHovered, marketplaceModalSelected } from "@/lib/marketplaceModalAnalytics";
import type { MarketplaceKey } from "@/marketplaces/registry";
import { onBeforeUnmount, onMounted, ref } from "vue";

const emit = defineEmits<{
  (e: "complete", marketplace: MarketplaceKey): void;
}>();

const selecting = ref<MarketplaceKey | null>(null);
const screenOpenedAt = ref(performance.now());

const marketplaceOptions: {
  id: MarketplaceKey;
  label: string;
  hint?: string;
  shortLabel?: string;
  logo?: string;
  icon?: string;
}[] = [
  {
    id: "generic",
    label: "Универсально",
    hint: "Под любые ваши цели",
    icon: "ai",
  },
  { id: "wb", label: "Wildberries", shortLabel: "WB", logo: wbLogo },
  { id: "ozon", label: "Ozon", shortLabel: "OZ", logo: ozonLogo },
  { id: "kaspi", label: "Kaspi", shortLabel: "KA", logo: kaspiLogo },
  { id: "flip", label: "Flip", shortLabel: "FL", logo: flipLogo },
  { id: "olx", label: "OLX", shortLabel: "OL", logo: olxLogo },
];

onMounted(() => {
  screenOpenedAt.value = performance.now();
  onboardingScreenViewed();
});

onBeforeUnmount(() => {
  if (!selecting.value) {
    onboardingScreenHidden("navigation_away");
  }
});

function handleSelect(marketplace: MarketplaceKey) {
  if (selecting.value) return;
  selecting.value = marketplace;
  const timeOnScreenMs = Math.round(performance.now() - screenOpenedAt.value);
  marketplaceModalSelected(marketplace);
  onboardingCompleted(marketplace, timeOnScreenMs);
  onboardingScreenHidden("completed");
  emit("complete", marketplace);
}
</script>

<style scoped lang="scss">
.login-screen {
  min-height: 100dvh;
  background: var(--sidebar-border);
  color: var(--foreground);
  padding: 0 clamp(16px, 3vw, 32px);
}

.login-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: color-mix(in oklch, var(--sidebar-border) 92%, transparent);
  border-bottom: 1px solid color-mix(in oklch, var(--border) 65%, transparent);
  backdrop-filter: blur(8px);
}

.onboarding-header__inner {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-header__brand {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.login-main {
  padding: 24px 0 40px;
}

.login-content {
  display: grid;
  gap: 20px;
}

.login-card {
  border-radius: calc(var(--radius) + 0.25rem);
  border: 1px solid color-mix(in oklch, var(--border) 75%, transparent);
  background: var(--card);
  padding: clamp(16px, 2.4vw, 24px);
  padding-bottom: clamp(24px, 3vw, 36px);
  display: flex;
  flex-direction: column;
  gap: 28px;
  text-align: center;
}

.login-card__header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-items: center;
  text-align: center;
}

.login-card__title {
  margin: 0;
  font-size: clamp(24px, 3.2vw, 34px);
  font-weight: 700;
  line-height: 1.2;
  text-wrap: balance;
}

.login-card__subtitle {
  margin: 0;
  font-size: clamp(18px, 2.4vw, 22px);
  line-height: 1.35;
  font-weight: 600;
  color: var(--foreground);
}

.onboarding-card__hint {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: var(--muted-foreground);
  max-width: 36ch;
  margin-inline: auto;
}

.onboarding-marketplace {
  width: min(100%, 420px);
  margin-inline: auto;
  display: grid;
  gap: 10px;
}

.onboarding-marketplace__option {
  width: 100%;
  display: grid;
  grid-template-columns: 3rem 1fr;
  align-items: center;
  gap: 14px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--card);
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    transform 160ms ease;
}

.onboarding-marketplace__option:hover:not(:disabled) {
  border-color: color-mix(in oklch, var(--ring) 55%, var(--border));
  background: color-mix(in oklch, var(--muted) 35%, var(--card));
}

.onboarding-marketplace__option:focus-visible {
  outline: none;
  border-color: color-mix(in oklch, var(--ring) 55%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 22%, transparent);
}

.onboarding-marketplace__option:disabled {
  opacity: 0.72;
  cursor: wait;
}

.onboarding-marketplace__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
}

.onboarding-marketplace__logo {
  display: block;
  width: 100%;
  max-height: 48px;
  object-fit: contain;
}

.onboarding-marketplace__icon-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: var(--muted);
  color: var(--muted-foreground);
}

.onboarding-marketplace__icon-fallback--text {
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.onboarding-marketplace__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.onboarding-marketplace__label {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--foreground);
}

.onboarding-marketplace__description {
  font-size: 13px;
  line-height: 1.35;
  color: var(--muted-foreground);
}

@media (min-width: 640px) {
  .login-main {
    padding-top: 40px;
  }
}
</style>
